import { type CSSProperties, useEffect, useState } from 'react';
import './checkout.css';

/* ── Shared types (mirrors GET /api/v2/onboarding/intent) ───────────────────── */
export interface CheckoutPlan {
  id: string;
  name: string;
  price: string;
  currency: string;
  billingCycle: string;
  tier: string;
  tokenLimit: number | null;
  slotCap: number | null;
  monthlyCredits: number | null;
  /** User-facing daily allowance. Drives the "N credits / day" lead benefit. */
  dailyCreditCap?: number | null;
  /** Admin-curated feature bullets: { "<bullet>": true }. Primary "Included" list. */
  features?: Record<string, unknown> | null;
}
export interface CheckoutCoupon {
  code: string;
  discountedPrice: number;
  discountAmount: number;
  expiresAt: string | null;
}
export interface CheckoutIntent {
  status: string;
  plan: CheckoutPlan | null;
  coupon: CheckoutCoupon | null;
  offerExpired: boolean;
  dismissedAt?: string | null;
}

/* ── useCountdown — presentational only; server re-validates at Razorpay handoff.
 *   Returns "6d 23h 12m" | "23h 12m" | "12m" while time remains, null once it
 *   elapses (or when there's no expiry). Polls every 30s, clears on unmount. */
function useCountdown(iso: string | null): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!iso) { setLabel(null); return; }
    const tick = () => {
      const ms = new Date(iso).getTime() - Date.now();
      if (!Number.isFinite(ms) || ms <= 0) { setLabel(null); return; }
      const d = Math.floor(ms / 864e5);
      const h = Math.floor((ms % 864e5) / 36e5);
      const m = Math.floor((ms % 36e5) / 6e4);
      setLabel(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [iso]);
  return label;
}

/* ── Honest, data-driven fallback list — used when a plan has no admin-curated
 *   features. Only tokenLimit==null means "Unlimited"; every other null OMITS
 *   its row (never invent an allowance). ────────────────────────────────────── */
/** The concrete user-facing allowance line: credits/day when the plan grants
 *  credits (what we sell), else the internal token cap. */
function allowanceLine(plan: CheckoutPlan): string {
  const nf = (n: number) => n.toLocaleString('en-IN');
  if (plan.dailyCreditCap != null) return `up to ${nf(plan.dailyCreditCap)} credits / day`;
  return plan.tokenLimit == null ? 'Unlimited tokens' : `${nf(plan.tokenLimit)} tokens / 30 days`;
}

function grantsFor(plan: CheckoutPlan): string[] {
  const nf = (n: number) => n.toLocaleString('en-IN');
  const out: string[] = [];
  out.push(allowanceLine(plan));
  if (plan.monthlyCredits != null) out.push(`${nf(plan.monthlyCredits)} monthly credits`);
  if (plan.slotCap != null) out.push(`${nf(plan.slotCap)} connector slot${plan.slotCap === 1 ? '' : 's'}`);
  const evergreen: Record<string, string[]> = {
    pro: ['Priority model lane', 'Full chat history'],
    power: ['Highest-priority lane', 'Team connectors'],
    enterprise: ['Dedicated support', 'SSO & audit log'],
    free: [],
  };
  out.push(...(evergreen[plan.tier] ?? []));
  return out;
}

/** Flatten the admin features jsonb ({ "<bullet>": true }) into clean, deduped
 *  bullet strings — splitting stray embedded "•", trimming, dropping falsy. */
function featureBullets(features?: Record<string, unknown> | null): string[] {
  if (!features) return [];
  const out: string[] = [];
  for (const [key, val] of Object.entries(features)) {
    if (!val) continue;
    for (const part of key.split('•')) {
      const s = part.trim().replace(/\s+/g, ' ');
      if (s && !out.some((x) => x.toLowerCase() === s.toLowerCase())) out.push(s);
    }
  }
  return out;
}

/** A trailing summary sentence (ends in "." with a few words) reads as a tagline,
 *  not a checklist item. */
function looksLikeTagline(s: string): boolean {
  return /\.$/.test(s) && s.split(/\s+/).length >= 4;
}

/** Primary benefit set for the card: admin-curated features when present (with a
 *  trailing summary lifted to a tagline + the concrete token allowance prepended
 *  when the bullets don't already state one), else the derived fallback list. */
function benefitsFor(plan: CheckoutPlan): { tagline: string | null; checklist: string[] } {
  const bullets = featureBullets(plan.features);
  if (bullets.length === 0) return { tagline: null, checklist: grantsFor(plan) };

  let tagline: string | null = null;
  let list = bullets;
  const last = bullets[bullets.length - 1];
  if (bullets.length > 1 && last && looksLikeTagline(last)) {
    tagline = last;
    list = bullets.slice(0, -1);
  }
  // Pin the Agent Studio unlocks to the front (the premium tempter). Postgres
  // jsonb sorts object keys by length, so the long "Agent Studio …" bullets land
  // last — and the card caps the list, which would drop them off the end.
  const isStudio = (s: string) => /agent studio/i.test(s);
  list = [...list.filter(isStudio), ...list.filter((s) => !isStudio(s))];
  // Prepend the concrete allowance (credits/day when sold, else tokens) unless a
  // curated bullet already states that unit.
  const unitRe = plan.dailyCreditCap != null ? /\bcredits?\b/i : /\btokens?\b/i;
  const withAllowance = list.some((s) => unitRe.test(s)) ? list : [allowanceLine(plan), ...list];
  return { tagline, checklist: withAllowance.slice(0, 6) };
}

function Check() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export interface CheckoutCardProps {
  plan: CheckoutPlan;
  coupon: CheckoutCoupon | null;
  offerExpired: boolean;
  onPayNow: () => void;
  onSecondary: () => void;
  secondaryLabel: string;
  busy?: boolean;
  /** id of the plan <h1>, used by the modal to move initial focus for a11y. */
  titleId?: string;
}

/**
 * CheckoutCard — the one presentational "statement" shared by /welcome and the
 * in-chat modal. Pre-purchase, future-tense framing (never reads as charged);
 * espresso CTA is the only filled element; gold confined to the live offer.
 */
export function CheckoutCard({ plan, coupon, offerExpired, onPayNow, onSecondary, secondaryLabel, busy, titleId = 'rcpt-title' }: CheckoutCardProps) {
  const cur = plan.currency === 'INR' ? '₹' : `${plan.currency} `;
  const yearly = plan.billingCycle.toUpperCase() === 'YEARLY';
  const per = yearly ? 'yr' : 'mo';

  const liveOffer = !!coupon && !offerExpired;
  const countdown = useCountdown(liveOffer ? coupon!.expiresAt : null);
  // Offer stays "active" while a live coupon has no expiry OR still has time left.
  const offerActive = liveOffer && (!coupon!.expiresAt || countdown != null);
  const price = offerActive ? coupon!.discountedPrice : Number(plan.price);
  const { tagline, checklist } = benefitsFor(plan);

  return (
    <article className="rcpt" aria-labelledby={titleId}>
      <span className="rcpt__crest">
        <img className="rcpt__logo" src="/brew-logo.png" alt="Breww.ai" width={40} height={40} />
        <span className="rcpt__steam" aria-hidden="true" />
      </span>

      <p className="rcpt__eyebrow">You&rsquo;re upgrading to</p>
      <h1 id={titleId} className="rcpt__plan" tabIndex={-1}>{plan.name}</h1>
      <p className="rcpt__cycle">Billed {yearly ? 'yearly' : 'monthly'}</p>
      {tagline && <p className="rcpt__tagline">{tagline}</p>}

      <p className="rcpt__price">
        <span className="rcpt__cur">{cur.trim()}</span>
        <span className="rcpt__amt">{price.toLocaleString('en-IN')}</span>
        <span className="rcpt__per">/ {per}</span>
      </p>

      <div className="rule"><span className="rule__label">Included</span></div>

      <ul className="rcpt__list">
        {checklist.map((g, i) => (
          <li key={g} className="rcpt__item" style={{ '--i': i } as CSSProperties}>
            <Check />
            <span>{g}</span>
          </li>
        ))}
      </ul>

      {offerActive && (
        <>
          <div className="rule rule--gold"><span className="rule__label">Offer</span></div>
          <div className="offer">
            {/* Auto-applied promos mint an internal BREW-xxxx code the user never
                types — show a clean "Offer applied" chip instead of the raw code.
                A real, memorable promo code (e.g. a typed one) still shows. */}
            <span className="offer__code">{/^BREW-[A-Z0-9]+$/i.test(coupon!.code) ? 'Offer applied' : coupon!.code}</span>
            <p className="offer__prices">
              <s className="offer__was">{cur}{Number(plan.price).toLocaleString('en-IN')}</s>
              <span className="offer__now">{cur}{coupon!.discountedPrice.toLocaleString('en-IN')}</span>
            </p>
            <p className="offer__save">You save {cur}{coupon!.discountAmount.toLocaleString('en-IN')}</p>
            {countdown && (
              <p className="offer__ends">
                <span className="dot" aria-hidden="true" />
                Ends in <time>{countdown}</time>
              </p>
            )}
          </div>
        </>
      )}

      {offerExpired && (
        <p className="offer__lapsed">This offer has ended — standard price shown.</p>
      )}

      <div className="rule rule--plain" />

      <div className="rcpt__actions">
        <button type="button" className="btn-espresso" onClick={onPayNow} disabled={busy}>
          Pay &amp; unlock {plan.name} now
        </button>
        <button type="button" className="btn-quiet" onClick={onSecondary} disabled={busy}>
          {secondaryLabel}
        </button>
      </div>

      <p className="rcpt__trust">
        Secure checkout <i>·</i> Cancel anytime <i>·</i> Free stays fully usable
      </p>
    </article>
  );
}
